import React, { useState } from "react";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext } from "@dnd-kit/sortable";
import { useOverviewContext } from "@features/overview-v2/hooks";
import { type OverviewSavedView } from "@features/overview-v2/types";
import { SyncViewReordering } from "@features/saved-views/sync-view-reordering";
import { IconButton } from "@shared/buttons";

import { SavedViewsMenuRow } from "./saved-views-menu-row";

interface ReorderSavedViewsProps {
  readonly onSelected: (view: OverviewSavedView) => void;
  readonly onCancel: () => void;
}

export const ReorderSavedViews = ({
  onSelected,
  onCancel,
}: ReorderSavedViewsProps) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const {
    selectedSavedView,
    updateSavedViewOrdering,
    updateSavedViewOrderingToAllAccountManagers,
    savedViews,
  } = useOverviewContext();

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      prettyLogEventPage({
        domain: "Saved Views",
        action: "Other",
        additionalActionInfo: "Reorder Saved Views",
      });
      await updateSavedViewOrdering(active, over, {
        errorMessage: "There was an error reordering the view.",
      });
    }
  };

  const sensors = useSensors(useSensor(PointerSensor));

  return (
    <div className="relative">
      <div className="px-4 mt-1 mb-2">
        <div>
          <IconButton
            className="text-cosmic text-xs font-bold px-0"
            leftIcon={<i className="fa-regular fa-sharp fa-arrow-left" />}
            onClick={onCancel}
          >
            BACK
          </IconButton>
          <SyncViewReordering
            updateSavedViewOrderingToAllAccountManagers={
              updateSavedViewOrderingToAllAccountManagers
            }
          />
        </div>
      </div>
      <div className="max-h-[300px] overflow-y-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext items={savedViews}>
            {savedViews.map((savedView) => (
              <SavedViewsMenuRow
                key={savedView.id}
                savedView={savedView}
                isReordering
                isSelected={savedView.id === selectedSavedView?.id}
                onClick={() => onSelected(savedView)}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};
